import type { CatalogProductStatus } from '../services/api';
import {
  discontinuedInSyncRows,
  storeRequestCatalogStatus,
  type StoreCatalogRow,
} from './storeCatalogVisibility';

/**
 * Store Product Browser data-source choices.
 *
 * The default view keeps discontinued rows the store is still syncing. The
 * server applies that rule itself: an Active page is requested with
 * `includeSyncedDiscontinued`, and `GET /store/products-for-sync` merges the
 * store's synced discontinued products into the page it cuts (MOT-607). The
 * browser used to decide client-side whether to widen the request to `all` at
 * `limit=10000` from the counts response (#161), which read the whole catalog
 * to show one page and made the grid wait for the counts call first. The counts
 * response still reports `discontinuedInSync`, for the facet badges.
 *
 * Only the sync-status, match and lifecycle filters (and the discontinued-in-sync
 * drill-down), which narrow rows in the browser, still fetch the full store
 * dataset and paginate client-side.
 */

export type StoreSyncStatusFilter = 'all' | 'in_sync' | 'not_in_sync' | 'sync_issues';

export function filterStoreSyncRows<T extends { id: string }>(rows: T[], filter: StoreSyncStatusFilter,
  selectedIds: Set<string>, issueIds: Set<string>): T[] {
  if (filter === 'in_sync') return rows.filter((row) => selectedIds.has(row.id));
  if (filter === 'not_in_sync') return rows.filter((row) => !selectedIds.has(row.id));
  if (filter === 'sync_issues') return rows.filter((row) => issueIds.has(row.id));
  return rows;
}

export type StoreCatalogCountsHint = {
  discontinuedInSync?: number;
  total?: number;
} | null | undefined;

/** React-query status for GET /store/catalog-counts. */
export type StoreCountsQueryStatus = {
  isLoading: boolean;
  isError: boolean;
  isSuccess: boolean;
};

export type StoreBrowserDataSourceInput = {
  isStoreScoped: boolean;
  catalogStatus: CatalogProductStatus;
  syncStatusFilter: StoreSyncStatusFilter;
  discontinuedInSyncOnly: boolean;
  /**
   * Counts response. `undefined` / `null` means the payload is missing — do not
   * treat that as "the store syncs nothing discontinued" while the query is
   * still in flight. Pair with `storeCountsStatus` so a settled error can
   * enable the grid without waiting forever.
   */
  storeCatalogCounts: StoreCatalogCountsHint;
  /**
   * Counts query status from react-query (`isLoading` / `isError` / `isSuccess`).
   * Loading must wait (#161). Error with no payload must widen, not take the
   * cheap Active-only path.
   */
  storeCountsStatus?: StoreCountsQueryStatus;
  /**
   * Rows from the universe query. Present only when that query ran.
   * Must never decide `storeSyncsDiscontinued` — that was #161's circularity.
   */
  storeUniverseRows?: StoreCatalogRow[];
  syncSelectedIds?: Set<string>;
  /**
   * "Matched to my store" filter is set (flag `product_browser_match_filter`).
   * It narrows rows in the browser like the sync filter, so it needs the full
   * store dataset and client pagination too.
   */
  matchFilterActive?: boolean;
};

export type StoreBrowserGridRequest = {
  /** The path is chosen; the grid no longer waits for the counts call. */
  ready: boolean;
  /** Active request was widened to `all` so discontinued-in-sync rows come back (client-paginated paths only). */
  widened: boolean;
  clientPaginated: boolean;
  catalogStatus: CatalogProductStatus;
  /**
   * The server merges the store's synced discontinued products into this Active
   * page, so the rows come back already following the default-view rule.
   */
  includeSyncedDiscontinued: boolean;
};

export type StoreBrowserDataSource = {
  storeSyncsDiscontinued: boolean | undefined;
  storeWidensForSyncedDiscontinued: boolean;
  shouldUseFullStoreSyncDataset: boolean;
  needsStoreUniverse: boolean;
  tabCountsFromUniverse: boolean;
  awaitingStoreCounts: boolean;
  gridRequest: StoreBrowserGridRequest;
  facetCountSource: 'universe' | 'counts';
  syncCountDenominatorSource: 'universe' | 'counts';
};

/**
 * Read only from the counts response. Never from universe rows.
 *
 * `undefined` = counts have not arrived; publishing `false` here is how the
 * default view settled on the cheap path and dropped in-sync discontinued rows.
 */
export const storeSyncsDiscontinuedFromCounts = (
  isStoreScoped: boolean,
  storeCatalogCounts: StoreCatalogCountsHint,
): boolean | undefined => {
  if (!isStoreScoped) return false;
  if (storeCatalogCounts == null) return undefined;
  return (storeCatalogCounts.discontinuedInSync || 0) > 0;
};

export const resolveStoreBrowserDataSource = (
  input: StoreBrowserDataSourceInput,
): StoreBrowserDataSource => {
  const {
    isStoreScoped,
    catalogStatus,
    syncStatusFilter,
    discontinuedInSyncOnly,
    storeCatalogCounts,
    storeCountsStatus,
    matchFilterActive = false,
  } = input;

  // `storeUniverseRows` is accepted so callers can pass what they have; it is
  // intentionally unused for `storeSyncsDiscontinued`.
  // Informational only (the badges): the grid request no longer depends on it.
  const storeSyncsDiscontinued = storeSyncsDiscontinuedFromCounts(
    isStoreScoped,
    storeCatalogCounts,
  );
  void storeCountsStatus;
  const narrowsInBrowser = syncStatusFilter !== 'all' || matchFilterActive;
  const needsStoreUniverse =
    isStoreScoped && (narrowsInBrowser || discontinuedInSyncOnly);
  // Only a filter applied in the browser needs the whole store dataset.
  const shouldUseFullStoreSyncDataset = needsStoreUniverse;
  const tabCountsFromUniverse = needsStoreUniverse;
  // The server applies the default-view rule to a paginated Active page.
  const includeSyncedDiscontinued =
    isStoreScoped && catalogStatus === 'active' && !shouldUseFullStoreSyncDataset;
  // Whether the Active view shows synced discontinued rows: on the server-paged
  // path always (the server merges them in), on a client-paginated path once the
  // request is widened to `all`.
  const storeWidensForSyncedDiscontinued =
    isStoreScoped && catalogStatus === 'active';
  const requestCatalogStatus = shouldUseFullStoreSyncDataset
    ? storeRequestCatalogStatus(catalogStatus)
    : catalogStatus;

  return {
    storeSyncsDiscontinued,
    storeWidensForSyncedDiscontinued,
    shouldUseFullStoreSyncDataset,
    needsStoreUniverse,
    tabCountsFromUniverse,
    awaitingStoreCounts: false,
    gridRequest: {
      ready: true,
      widened: shouldUseFullStoreSyncDataset && catalogStatus === 'active',
      clientPaginated: shouldUseFullStoreSyncDataset,
      catalogStatus: requestCatalogStatus,
      includeSyncedDiscontinued,
    },
    facetCountSource: tabCountsFromUniverse ? 'universe' : 'counts',
    syncCountDenominatorSource: tabCountsFromUniverse ? 'universe' : 'counts',
  };
};

/**
 * The filters a client-paginated grid request carries beside the catalog status.
 * `sort` is the picked sort; the request omits the default (`name_asc`).
 */
export type StoreGridRequestFilters = {
  search?: string;
  category?: string;
  productType?: string;
  brand?: string;
  addedSince?: string;
  sort?: string;
};

/**
 * Whether a client-paginated grid request is the universe request itself: the
 * whole brand-scoped dataset at `limit=10000` with no other filter, which the
 * universe query already fetches for the facet counts and brand options. When
 * it is, the grid reads the universe query instead of sending the same URL a
 * second time (MOT-628: that duplicate, retried, is what filled the `api`
 * instance). A request that narrows by search, category, type, brand, date or
 * a non-default sort is still its own fetch.
 */
export const gridRequestReadsUniverse = (
  gridRequest: Pick<StoreBrowserGridRequest, 'clientPaginated' | 'includeSyncedDiscontinued'>,
  filters: StoreGridRequestFilters,
): boolean => {
  if (!gridRequest.clientPaginated || gridRequest.includeSyncedDiscontinued) return false;
  const blank = (value: string | undefined) => !(value || '').trim();
  return (
    blank(filters.search) &&
    blank(filters.category) &&
    blank(filters.productType) &&
    blank(filters.brand) &&
    blank(filters.addedSince) &&
    (blank(filters.sort) || filters.sort === 'name_asc')
  );
};

/** Denominator for "Not in sync". Must not read the universe when it was not fetched. */
export const resolveSyncCountDenominator = (input: {
  tabCountsFromUniverse: boolean;
  storeUniverseLength: number;
  storeCatalogTotal: number | undefined;
}): number =>
  input.tabCountsFromUniverse ? input.storeUniverseLength : input.storeCatalogTotal || 0;

export const buildSyncCountMap = (input: {
  isStoreScoped: boolean;
  inSyncCount: number;
  denominator: number;
}): Map<string, number> => {
  const map = new Map<string, number>();
  if (!input.isStoreScoped) return map;
  map.set('in_sync', input.inSyncCount);
  map.set('not_in_sync', Math.max(0, input.denominator - input.inSyncCount));
  return map;
};

/** Banner / drill-down count from the rows the current filters actually returned. */
export const countDiscontinuedInSyncRows = <T extends StoreCatalogRow>(
  products: T[],
  syncSelectedIds: Set<string>,
): number => discontinuedInSyncRows(products, syncSelectedIds).length;
